import {kFakeFlashGrades, kFakeSubthemesWithLevelAs} from "../../../services/Faker";
import {get_flash_grades} from "../../../api/grade";
import {isArrayNullUndefinedOrEmpty, isNullOrUndefined} from "../../../services/Validator";
import {get_subthemes_with_levelAs_from_grade} from "../../../api/subtheme";
import {useEffect, useState} from "react";
import {
    kSystemGradesError, kFlashSubthemesError, kEmptyResponseError
} from "../../../constants/errors";
import {sendErrorToSentry} from "../../../services/Sentry";
import {Environment} from "../../../enums/Environment";
import CoursesCoreContent from "./CoursesCoreContent";

export default function CoursesContent({ selectedGrade,
                                         setFlashGrade }) {
    // Used in CoursesScreen to handle flash parameters selection
    // which is made of Grade and Subthemes selection

    // PROPERTIES
    // ---- Grades
    const [isGradesLoading, setIsGradesLoading] = useState(true)
    const [gradesError, setGradesError] = useState(null)
    const [grades, setGrades] = useState([])
    // ---- Subthemes
    const [isSubthemesLoading, setIsSubthemesLoading] = useState(false)
    const [subthemesError, setSubthemesError] = useState(null)
    const [subthemes, setSubthemes] = useState([])
    const [selectedSubtheme, setSelectedSubtheme] = useState(null)
    const [levelAs, setLevelAs] = useState([])
    const [selectedLevelAs, setSelectedLevelAs] = useState([])

    // HOOKS
    // ---- Triggered at rendering : set Grades
    useEffect(() => {
        getFlashGrades()
    }, [])

    // BUILD
    return (
        <div className={"grow w-full flex flex-col justify-start items-center min-h-0"}>
            <CoursesCoreContent isGradesLoading={isGradesLoading}
                                grades={grades}
                                gradesError={gradesError}
                                getFlashGrades={getFlashGrades}
                                selectedGrade={selectedGrade}
                                setFlashGrade={getSubthemesWithLevelAsFromGrade}
                                isSubthemesLoading={isSubthemesLoading}
                                subthemesError={subthemesError}
                                subthemes={subthemes}
                                selectSubtheme={selectSubtheme}
                                selectedSubtheme={selectedSubtheme}
                                levelAs={levelAs}
                                selectedLevelAs={selectedLevelAs}
                                pickLevelA={pickLevelA}
                                clearLevelAs={clearLevelAs} />
        </div>
    );

    // METHODS
    // ---- API : Fetch flash levels from the api
    function getFlashGrades() {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setGrades(kFakeFlashGrades)
            setIsGradesLoading(false)
            setGradesError(null)
            return
        }
        // ---- For .env.faker >>>>

        // Switch loader on and clear error
        setIsGradesLoading(true)
        setGradesError(null)
        // Perform
        get_flash_grades()
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setGradesError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching Grades from CoursesContent")
                } else {
                    setGrades(res)
                }
            }).catch((err) => {
                console.error(err)
                setGradesError(kSystemGradesError)
                sendErrorToSentry(kSystemGradesError.sentry, "CoursesContent")
        }).finally(() => {
            setIsGradesLoading(false)
        })
    }

    // ---- API : Fetch Subthemes with LevelAs based on a specific Grade and Theme
    function getSubthemesWithLevelAsFromGrade(selectedGrade) {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setSubthemes(kFakeSubthemesWithLevelAs)
            setFlashGrade(selectedGrade)
            setIsSubthemesLoading(false)
            setSubthemesError(null)
            return
        }
        // ---- For .env.faker >>>>

        // Check input parameters
        if (isNullOrUndefined(selectedGrade?.id))
            return;
        // Perform setFlashGrade from MetaCoursesScreen
        setFlashGrade(selectedGrade)
        // Reset screen state
        resetScreenState()
        // Switch loader on and clear error
        setIsSubthemesLoading(true)
        setSubthemesError(null)
        // Perform request
        get_subthemes_with_levelAs_from_grade(selectedGrade?.id)
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setSubthemesError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching Subthemes from CoursesContent")
                } else {
                    setSubthemes(res)
                }
            }).catch((err) => {
            console.error(err)
            setSubthemesError(kFlashSubthemesError)
            sendErrorToSentry(kFlashSubthemesError.sentry)
        }).finally(() => {
            setIsSubthemesLoading(false)
        })
    }

    // ---- API : Fetch exerciser problems
    // function fetchExerciserProblem(levelAIdentifier) {
    //     // Defense
    //     // ---- Current screen URL identifier parameter
    //     if (isNullOrUndefined(levelAIdentifier) || !isNumber(levelAIdentifier) || levelAIdentifier < 0) {
    //         setIsExerciserLoading(false)
    //         setExerciserError("Malformed id parameter")
    //         return
    //     }
    //
    //     // ---- Set loader on and clear potential error (retry)
    //     setIsExerciserLoading(true)
    //     setExerciserError(null)
    //
    //     // Perform
    //     get_exerciser_problem(levelAIdentifier)
    //         .then((res) => {
    //             fetchExerciserProblemCallback(levelAIdentifier, res)
    //         }).catch((err) => {
    //         setExerciserError(kExerciserFetchError)
    //         console.error(err)
    //     }).finally(() => {
    //         setIsExerciserLoading(false)
    //     })
    // }

    // ---- ---- ----
    // USER INTERACTION
    // ---- ---- ----
    // ---- LOGIC : Add picked levelA to selectedLevelAs (replace toggleLevelA)
    function pickLevelA(currentLevelA) {
        setSelectedLevelAs([currentLevelA]);
    }

    // ---- LOGIC : Clear selectedLevelAs (replace toggleLevelA)
    function clearLevelAs() {
        setSelectedLevelAs([])
    }

    // ---- Select a specific subtheme
    function selectSubtheme(subtheme) {
        // Check input parameter
        if (isNullOrUndefined(subtheme))
            return
        // Set current selected Subtheme
        setSelectedSubtheme(subtheme)
        // Set current displayed LevelAs
        setLevelAs(subtheme["subthemeLevelAs"])
    }

    // ---- ---- ----
    // OTHERS
    // ---- ---- ----
    // ---- Reset screen global state
    function resetScreenState() {
        setSubthemes([])
        setSelectedSubtheme(null)
        setLevelAs([])
        setSelectedLevelAs([])
    }
}